/**
 * Marketing-client-only icon registry. Swapped in for `"./iconRegistry"` (imported from
 * `Icon.tsx`) by the plugin in `vite.config.ssr.ts` when `SSR_TARGET=client-marketing` — the SPA
 * and the SSR server keep the default `iconRegistry.ts` (the full, always-synchronous registry)
 * because neither of them route-splits, so there is nothing to lazily fetch icons against.
 *
 * The marketing client ships only `marketingCoreIcons.json`'s ~29 names up front — the icons the
 * homepage's server-rendered HTML actually contains, plus the couple any marketing page renders
 * client-side (`close`, `playTriangle`) — and fetches the remaining ~613-icon Icons.json as its
 * own lazy chunk, only once something happening after hydration could actually need one of them.
 *
 * That "something" is any of pointerdown/keydown/touchstart/mouseover/scroll: each is a plausible
 * precursor to an interaction that reveals a non-core icon (a menu opening, a hover state, a
 * scroll-triggered section), so kicking the fetch off on whichever fires first hides its latency
 * behind the interaction that would have needed it, instead of behind first paint. A reader who
 * lands and never interacts never downloads it at all.
 */
import { useSyncExternalStore } from "react";
import coreIcons from "virtual:stippl-marketing-core-icons";

export const IS_PARTIAL_ICON_REGISTRY = true;

let full: Record<string, any> | null = null;
let loading: Promise<void> | null = null;
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
	listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

export function requestFullIconRegistry(): void {
	if (full || loading) return;
	loading = import("./Icons.json")
		.then((m) => {
			full = ((m as any).default ?? m) as Record<string, any>;
			version++;
			notify();
		})
		.catch((e) => {
			// Clear the in-flight marker so the next interaction or missing icon retries, rather
			// than leaving every non-core icon blank until a reload after one failed fetch.
			loading = null;
			console.error("[Icons] Full icon registry failed to load:", { error: e?.message || e });
		});
}

export function getIcon(name: string): any {
	return (coreIcons as any)[name] ?? full?.[name];
}

export function useIconRegistryVersion(): number {
	return useSyncExternalStore(subscribe, () => version, () => 0);
}

// Browser only (SSR has no window) — first interaction of any of these kinds requests the full
// registry, then every listener (including the ones that haven't fired yet) is torn down. `once:
// true` alone would only remove the listener that actually fired; the explicit removeEventListener
// loop clears the rest so a reader who scrolls once doesn't leave four dead listeners registered.
if (typeof window !== "undefined") {
	const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "mouseover", "scroll"] as const;
	const onInteraction = () => {
		requestFullIconRegistry();
		INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, onInteraction, true));
	};
	INTERACTION_EVENTS.forEach((evt) => window.addEventListener(evt, onInteraction, { passive: true, capture: true, once: true }));
}
